/**
 * SQL 튜닝 창 — 대상 등록부터 결과 리포트까지 한 작업으로(docs/features/20-sql-tuning.md).
 *
 * 단계: 1 대상 · 2 기초 자료 · 3 진단 · 4 개선안·검증 · 5 최종안 · 6 리포트.
 * **AI 없이** 모든 단계를 손으로 할 수 있고(AI 제외 빌드에서도 열린다), AI 가 있으면 단계마다 「AI 에게 요청」,
 * 그리고 「자동 진행」(수집 → 진단 → 개선안 → 검증 → 해석·최종안 → 리포트)을 쓸 수 있다.
 * **글은 AI, 측정은 기계** — 검증(실측·결과 대조·논리 읽기 판정)은 늘 서버가 같은 잣대로 한다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiActivity, FiPlus, FiTrash2, FiPlay, FiCpu, FiDownload, FiSearch, FiSquare, FiRefreshCw, FiExternalLink, FiArrowUp, FiArrowDown, FiMessageSquare, FiBookOpen, FiEdit2, FiX } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import { detectBindVariables, inferBindType } from '../../utils/sqlBinds';
import * as api from '../../services/sqlTuning';
import { monoWideText } from '../../utils/monoWide';
import { onGuardrailWaiting } from '../../utils/guardrailGate';
import PlanMarked from './PlanMarked';
import WritingGuideModal from './WritingGuideModal';
import AutoRunDialog from './AutoRunDialog';
import ReportView from './ReportView';
import { GuidePicker, CodeChips, matchKey } from './GuideCodes';
import * as guardrailApi from '../../services/guardrail';
import { guardExport } from '../../utils/guardrailGate';
import { ALT_DROP_REASONS, dropReasonPayload } from '../../utils/altDropReasons';
import memoWindow from '../common/memoWindow';
import WorkflowHead, { WorkflowRibbon, stepDirty } from '../common/WorkflowHead';
import './SqlTuningModal.css';

const STEPS = ['target', 'collect', 'diagnose', 'alternatives', 'final', 'report'];
// 단계가 아닌 탭 — 과정 이력(가이드라인 원칙 5). 번호 없이 맨 끝에 둔다
const TABS = [...STEPS, 'history'];
const ACTORS = { user: 'user', ai: 'ai', machine: 'machine' };
const SCOPES = ['query', 'index', 'all'];
const ORDERS = ['bufPerExec', 'buffers', 'elPerExec', 'elapsed', 'executions', 'diskReads'];
const fmt = (v) => (v == null || Number.isNaN(Number(v)) ? '-' : Number(v).toLocaleString());
const isOracle = (c) => !c.dbType || c.dbType === 'oracle';

/** 409 확인 요청(운영 실행·임시 인덱스)을 묻고 다시 부른다 — 서버가 확인 없이는 실행하지 않는다 */
async function withConfirms(call, confirm, t) {
  const flags = {};
  for (let i = 0; i < 3; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await call(flags);
    } catch (err) {
      const d = err && err.response && err.response.data;
      if (!d || err.response.status !== 409) throw err;
      if (d.errorType === 'SQL_TUNE_CONFIRM_PROD') {
        // eslint-disable-next-line no-await-in-loop
        const ok = await confirm({ title: t('common:stn.confirmProdTitle'), message: t('common:stn.confirmProdBody'), confirmText: t('common:stn.run'), variant: 'danger' });
        if (!ok) return null;
        flags.confirmProd = true;
      } else if (d.errorType === 'SQL_TUNE_CONFIRM_INDEX') {
        const lines = (d.preview || []).map(p => `${p.table} (${p.columns.join(', ')})${p.unique ? ' UNIQUE' : ''} — ${t('common:stn.rowsBlocks', { rows: fmt(p.numRows), blocks: fmt(p.blocks) })}`);
        // eslint-disable-next-line no-await-in-loop
        const ok = await confirm({ title: t('common:stn.confirmIndexTitle'), message: `${t('common:stn.confirmIndexBody')}\n\n${lines.join('\n')}`, confirmText: t('common:stn.run') });
        if (!ok) return null;
        flags.confirmIndex = true;
      } else throw err;
    }
  }
  return null;
}

function NewJobForm({ connections, defaultConnectionId, getEditorSql, onCreated, onCancel }) {
  const { t } = useTranslation();
  const conns = useMemo(() => connections.filter(isOracle), [connections]);
  const [connectionId, setConnectionId] = useState(defaultConnectionId && conns.some(c => c.id === defaultConnectionId) ? defaultConnectionId : (conns[0] && conns[0].id) || '');
  const [title, setTitle] = useState('');
  // 목록에서 고르며 자동으로 채운 이름인가 — 그렇다면 다른 SQL 을 고를 때 따라 바뀐다(사용자가 쓴 이름은 두고)
  const autoTitle = useRef(true);
  const [sql, setSql] = useState('');
  const [binds, setBinds] = useState([]);
  const [source, setSource] = useState({ source: 'manual', sqlId: null });
  const [pick, setPick] = useState({ q: '', orderBy: 'bufPerExec', rows: null, busy: false });
  const [busy, setBusy] = useState(false);
  // 「새 튜닝 작업」 을 누르면 양식이 새로 그려지고 SQL 칸으로 커서가 온다 — 이미 열린 양식에서 누르면 아무 변화가 없어
  // 「반응이 없다」 로 보였다(2026-10-26 신고)
  const sqlRef = useRef(null);
  useEffect(() => { if (sqlRef.current) sqlRef.current.focus(); }, []);

  const syncBinds = (text, known = binds) => {
    const names = detectBindVariables(text || '');
    setBinds(names.map(n => known.find(b => b.name === n) || { name: n, value: '', dataType: '' }));
  };
  const fromEditor = () => {
    const e = getEditorSql ? getEditorSql() : null;
    if (!e || !e.sql) { toast.info(t('common:stn.editorEmpty')); return; }
    setSql(e.sql);
    const known = Object.entries(e.binds || {}).map(([name, value]) => ({ name, value: String(value), dataType: inferBindType(value) === 'NULL' ? '' : inferBindType(value) }));
    syncBinds(e.sql, known);
    setSource({ source: 'manual', sqlId: null });
  };
  const search = async () => {
    if (!connectionId) return;
    setPick(p => ({ ...p, busy: true }));
    try {
      const r = await api.listSqlArea({ connectionId, q: pick.q, orderBy: pick.orderBy, limit: 50 });
      setPick(p => ({ ...p, rows: r.rows || [], busy: false }));
    } catch (err) { setPick(p => ({ ...p, busy: false })); toast.error(formatApiError(err, t)); }
  };
  const choose = async (row) => {
    try {
      const d = await api.sqlAreaDetail({ connectionId, sqlId: row.sqlId });
      setSql(d.sql);
      setBinds(d.binds || []);
      setSource({ source: 'sqlarea', sqlId: row.sqlId, bindSource: d.bindSource });
      if (autoTitle.current) setTitle(`${row.sqlId} · ${row.schema || ''}`);
      if (d.binds && d.binds.length && !d.bindSource) toast.info(t('common:stn.bindsNoValue'));
    } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const create = async () => {
    if (!connectionId || !sql.trim()) return;
    setBusy(true);
    try {
      const r = await api.createJob({ connectionId, title, target: { sql, binds, source: source.source, sqlId: source.sqlId } });
      onCreated(r.job);
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };

  return (
    <div className="stn-new">
      <div className="stn-row">
        <label className="stn-field"><span>{t('common:stn.connection')}</span>
          <select value={connectionId} onChange={e => setConnectionId(e.target.value)}>
            {conns.map(c => <option key={c.id} value={c.id}>{c.name}{c.connType ? ` (${c.connType})` : ''}</option>)}
          </select>
        </label>
        <label className="stn-field stn-grow"><span>{t('common:stn.title')}</span>
          <input value={title} onChange={e => { autoTitle.current = !e.target.value; setTitle(e.target.value); }} placeholder={t('common:stn.titlePh')} />
        </label>
      </div>
      {!conns.length && <div className="stn-note">{t('common:stn.noOracleConn')}</div>}
      <div className="stn-pick">
        <div className="stn-row">
          <span className="stn-label">{t('common:stn.pickFromSqlArea')}</span>
          <input className="stn-grow" value={pick.q} onChange={e => setPick(p => ({ ...p, q: e.target.value }))} placeholder={t('common:stn.searchPh')}
            onKeyDown={e => { if (e.key === 'Enter') search(); }} />
          <select value={pick.orderBy} onChange={e => setPick(p => ({ ...p, orderBy: e.target.value }))} aria-label={t('common:stn.orderBy')}>
            {ORDERS.map(o => <option key={o} value={o}>{t(`common:stn.order.${o}`)}</option>)}
          </select>
          <button type="button" className="app-btn" onClick={search} disabled={pick.busy || !connectionId}><FiSearch size={12} /> {t('common:stn.search')}</button>
        </div>
        {pick.rows && (
          <div className="stn-table-wrap stn-pick-list">
            <table className="stn-table">
              <thead><tr><th>sql_id</th><th>{t('common:stn.schema')}</th><th className="num">{t('common:stn.executions')}</th><th className="num">{t('common:stn.bufPerExec')}</th><th className="num">{t('common:stn.elPerExec')}</th><th>SQL</th></tr></thead>
              <tbody>
                {pick.rows.map(r => (
                  <tr key={`${r.instId}-${r.sqlId}`} className={source.sqlId === r.sqlId ? 'on' : ''} onClick={() => choose(r)}>
                    <td className="mono">{r.sqlId}</td><td>{r.schema}</td><td className="num">{fmt(r.executions)}</td>
                    <td className="num">{fmt(r.bufPerExec)}</td><td className="num">{r.elPerExec ?? '-'}</td><td className="mono stn-ellipsis">{r.head}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!pick.rows.length && <div className="stn-note">{t('common:stn.noRows')}</div>}
          </div>
        )}
      </div>
      <div className="stn-row stn-between">
        <span className="stn-label">{t('common:stn.sql')}</span>
        {getEditorSql && <button type="button" className="app-btn" onClick={fromEditor}>{t('common:stn.fromEditor')}</button>}
      </div>
      <textarea ref={sqlRef} className="stn-sql stn-sql-tall" rows={14} value={sql} onChange={e => { setSql(e.target.value); syncBinds(e.target.value); setSource({ source: 'manual', sqlId: null }); }} spellCheck={false} />
      <BindEditor binds={binds} onChange={setBinds} />
      <div className="stn-actions">
        {/* 꺼져 있는 까닭을 말한다 — 「SQL 목록에서 고르면 켜진다」 가 무슨 차이인지 묻는 신고가 있었다 */}
        {(!connectionId || !sql.trim()) && <span className="stn-note">{t(!connectionId ? 'common:stn.createNeedsConn' : 'common:stn.createNeedsSql')}</span>}
        <button type="button" className="app-btn" onClick={onCancel}>{t('common:ui.cancel')}</button>
        <button type="button" className="app-btn app-btn-primary" onClick={create} disabled={busy || !connectionId || !sql.trim()}><FiPlus size={12} /> {t('common:stn.create')}</button>
      </div>
    </div>
  );
}

function BindEditor({ binds, onChange, readOnly }) {
  const { t } = useTranslation();
  if (!binds.length) return <div className="stn-note">{t('common:stn.noBinds')}</div>;
  const set = (i, k, v) => onChange(binds.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  return (
    <table className="stn-table stn-binds">
      <thead><tr><th>{t('common:stn.bindName')}</th><th>{t('common:stn.bindValue')}</th><th>{t('common:stn.bindType')}</th></tr></thead>
      <tbody>
        {binds.map((b, i) => (
          <tr key={b.name}>
            <td className="mono">:{b.name}</td>
            <td><input value={b.value == null ? '' : b.value} placeholder="NULL" onChange={e => set(i, 'value', e.target.value)} disabled={readOnly} /></td>
            <td><input value={b.dataType || ''} placeholder={t('common:stn.bindTypePh')} onChange={e => set(i, 'dataType', e.target.value)} disabled={readOnly} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Metrics({ m }) {
  const { t } = useTranslation();
  if (!m) return null;
  return (
    <div className="stn-metrics">
      <span>{t('common:stn.m.buffers')} <b>{fmt(m.buffers)}</b></span>
      <span>{t('common:stn.m.reads')} <b>{fmt(m.reads)}</b></span>
      <span>{t('common:stn.m.rows')} <b>{fmt(m.actualRows ?? m.fetchedRows)}</b></span>
      <span>{t('common:stn.m.elapsed')} <b>{m.elapsedSec ?? m.wallSec ?? '-'}s</b></span>
      {m.sqlId && <span className="mono">sql_id {m.sqlId}</span>}
    </div>
  );
}

/**
 * 「지시를 붙여 AI 에게 다시 쓰기」(가이드라인 원칙 4) — 지금 적힌 내용(사람이 고친 것 포함)과 지시를 함께 보낸다.
 * 서버가 같은 검사(ai.pick → checkLinks)를 지나게 하고 이력에 지시를 남긴다. 고칠 내용이 있을 때만 보인다.
 */
function RewriteBar({ options, onRewrite, disabled }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [which, setWhich] = useState(options[0]);
  if (!options.length) return null;
  const go = async () => { const ok = await onRewrite(options.includes(which) ? which : options[0], text.trim()); if (ok) setText(''); };
  return (
    <div className="stn-rewrite">
      {options.length > 1 && (
        <select value={which} onChange={e => setWhich(e.target.value)} aria-label={t('common:stn.rewriteWhich')}>
          {options.map(o => <option key={o} value={o}>{t(`common:stn.rewriteSteps.${o}`)}</option>)}
        </select>
      )}
      <input className="stn-grow" value={text} onChange={e => setText(e.target.value)} placeholder={t('common:stn.rewritePh')}
        onKeyDown={e => { if (e.key === 'Enter' && text.trim() && !disabled) go(); }} maxLength={2000} />
      <button type="button" className="app-btn stn-ai" onClick={go} disabled={disabled || !text.trim()}><FiCpu size={12} /> {t('common:stn.rewrite')}</button>
    </div>
  );
}

/**
 * 튜닝 사례 참조 — 켜면 개선안·최종안 AI 단계에 닮은 사례를 **대조 자료로** 싣는다(진단에는 싣지 않는다 — 결론을 먼저 낸다).
 * 고른 사례가 없으면 서버가 닮은 것 상위 3건을 자동으로 고른다. 설정은 작업 문서의 caseRef — 「저장」 해야 남는다.
 */
function CaseRefBox({ jobId, value, onChange, onSave, disabled, catalog }) {
  const { t } = useTranslation();
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const v = value || { enabled: false, ids: [] };
  const find = async () => {
    setBusy(true);
    try { setFound(await api.findCases(jobId)); } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };
  const toggleId = (id) => onChange({ ...v, ids: v.ids.includes(id) ? v.ids.filter(x => x !== id) : [...v.ids, id].slice(0, 5) });
  return (
    <div className="stn-box stn-caseref">
      <div className="stn-row">
        <label title={t('common:stn.caseRefHint')}><input type="checkbox" checked={!!v.enabled} onChange={e => onChange({ ...v, enabled: e.target.checked })} /> <FiBookOpen size={12} /> {t('common:stn.caseRef')}</label>
        <span className="stn-note">{v.enabled ? (v.ids.length ? t('common:stn.caseRefPicked', { n: v.ids.length }) : t('common:stn.caseRefAuto')) : t('common:stn.caseRefOff')}</span>
        <span className="stn-grow" />
        <button type="button" className="app-btn" onClick={find} disabled={busy}>{t('common:stn.caseFind')}</button>
      </div>
      {found && (
        <div className="stn-fade">
          {!found.cases.length && <div className="stn-note">{found.sharedCorpus ? t('common:stn.caseNone') : t('common:stn.caseNoneOwnOnly')}</div>}
          {found.cases.map(c => (
            <label key={c.id} className="stn-case">
              <input type="checkbox" checked={v.ids.includes(c.id)} onChange={() => toggleId(c.id)} />
              <span className="stn-case-body">
                {/* 일치 근거 — 위에 나왔다고 맞는 사례가 아니다(증상만 같으면 원인이 다를 수 있다) */}
                {c.match && <span className={`stn-match m-${c.match.tier}`}>{t(matchKey(c.match))}</span>}
                <b>{c.title}</b>{c.mine ? ` · ${t('common:stn.caseMine')}` : ''}{c.improvement ? ` · ${c.improvement}` : ''}
                {c.perfBasis === 'estimated' ? <span className="stn-note"> · {t('common:stn.caseEstimated')}</span> : null}
                {(c.topics || []).length > 0 && <span className="stn-chips">{c.topics.map(x => <span key={x} className="stn-chip">{((catalog || []).find(tp => tp.code === x) || {}).label || x}</span>)}</span>}
                {c.body && <span className="stn-note">{c.body}</span>}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

CaseRefBox.propTypes = {
  jobId: PropTypes.string.isRequired,
  value: PropTypes.shape({ enabled: PropTypes.bool, ids: PropTypes.arrayOf(PropTypes.string) }),
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  catalog: PropTypes.arrayOf(PropTypes.object),
};

const PROVIDER_NAME = { claude: 'Claude', codex: 'Codex' };

/** 대안을 가이드의 검증 순서(서버가 준 order)대로 — 순서에 없는 것은 뒤에 원래 순서로 */
const orderedAlts = (alts, guide) => {
  const pos = new Map(((guide && guide.order) || []).map((x, i) => [x.id, i]));
  return [...alts].map((a, i) => ({ a, i })).sort((x, y) => ((pos.has(x.a.id) ? pos.get(x.a.id) : 999) - (pos.has(y.a.id) ? pos.get(y.a.id) : 999)) || (x.i - y.i)).map(x => x.a);
};

/** 진단 항목이 짚은 계획 줄 Id — planIds, 없으면 지점 글의 「Id N」(서버 model.planIdsOf 와 같은 규칙) */
const planIdsOf = (it) => {
  const src = Array.isArray(it && it.planIds) && it.planIds.length
    ? it.planIds
    : [...String((it && it.point) || '').matchAll(/\bId\s*(\d{1,4})\b/gi)].map(m => m[1]);
  return [...new Set(src.map(Number).filter(n => Number.isInteger(n) && n >= 0))];
};
const itemLabel = (it, i) => it.id || `#${i + 1}`;
const fmtSec = (ms) => (ms == null ? '-' : `${Math.max(0, Math.round(ms / 1000))}`);

/**
 * AI 단계 진행 — 「AI 가 쓰는 중…」 만으로는 어디에 연결됐는지·요청이 갔는지·응답이 오고 있는지 모른다(신고 2026-10-08).
 * 서버의 진행 상황(sqlTuning/progress.js)을 1초마다 읽는다. 내용은 없다 — 단계·LLM·계정 경로·모델·시각·글자 수.
 * 가드레일 확인을 기다리는 중이면 그렇게 말한다(확인 창이 다른 창에 가려 멈춘 줄 알았다). 「중지」 는 LLM 호출을 끊는다.
 */
function AiProgress({ jobId, step, provider }) {
  const { t } = useTranslation();
  const [p, setP] = useState(null);
  const [guard, setGuard] = useState(null);
  const [stopping, setStopping] = useState(false);
  useEffect(() => onGuardrailWaiting(setGuard), []);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try { const r = await api.aiProgress(jobId); if (alive) setP(r.progress || null); } catch (_e) { /* 표시용 — 실패해도 단계는 계속 */ }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => { alive = false; clearInterval(id); };
  }, [jobId]);
  const stop = async () => {
    setStopping(true);
    try { await api.aiCancel(jobId); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const prov = PROVIDER_NAME[(p && p.provider) || provider] || (p && p.provider) || provider || 'AI';
  const stage = guard ? 'guardrail' : ((p && p.running && p.stage) || 'prepare');
  const detail = {
    guardrail: t('common:stn.aiProg.guardrail'),
    prepare: t('common:stn.aiProg.prepare'),
    lookup: t('common:stn.aiProg.lookup'),
    connecting: t('common:stn.aiProg.connecting'),
    requested: t('common:stn.aiProg.requested', { chars: (p && p.promptChars ? p.promptChars : 0).toLocaleString(), sec: fmtSec(p && p.waitingMs) }),
    receiving: t('common:stn.aiProg.receiving', { chars: (p && p.chars ? p.chars : 0).toLocaleString() }),
    checking: t('common:stn.aiProg.checking'),
  }[stage] || t('common:stn.aiProg.prepare');
  return (
    <div className="stn-progress stn-aiprog" role="status" aria-live="polite">
      <div className="stn-row">
        <b>{t(`common:stn.aiSteps.${step}`, { defaultValue: step })}</b>
        <span>· {prov}{p && p.authSource ? ` · ${t(`common:stn.aiAuth.${p.authSource}`, { defaultValue: p.authSource })}` : ''}{p && p.model ? ` · ${p.model}` : ''}</span>
        <span className="stn-grow" />
        <span className="stn-note">{t('common:stn.aiProg.elapsed', { sec: fmtSec(p && p.elapsedMs) })}</span>
        <button type="button" className="app-btn app-btn-danger" onClick={stop} disabled={stopping || guard}><FiSquare size={11} /> {t('common:stn.stop')}</button>
      </div>
      <div className="stn-aiprog-stage" key={stage}>{detail}</div>
    </div>
  );
}

/** 걸린 시간 — 수집·검증은 SQL 을 끝까지 돌려 오래 걸린다. 멈춘 것인지 도는 것인지 보이게 */
function Elapsed({ since }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  return <span className="stn-note">{t('common:stn.aiProg.elapsed', { sec: fmtSec(now - since) })}</span>;
}

Elapsed.propTypes = { since: PropTypes.number.isRequired };

AiProgress.propTypes = { jobId: PropTypes.string.isRequired, step: PropTypes.string.isRequired, provider: PropTypes.string };

RewriteBar.propTypes = { options: PropTypes.arrayOf(PropTypes.string).isRequired, onRewrite: PropTypes.func.isRequired, disabled: PropTypes.bool };

const SqlTuningModal = ({ open, onClose, connections, defaultConnectionId, getEditorSql, onOpenPlans, aiEnabled, aiProvider, focus, onAskAssistant, onRegisterCase, onOpenGuides, restore, onView }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState('');
  const [job, setJob] = useState(null);
  const [problems, setProblems] = useState([]);
  const [step, setStep] = useState('target');
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);           // 진행 중인 일의 이름
  const [busySince, setBusySince] = useState(0);
  // 마지막 실패 — 알림(toast)은 몇 초 뒤 사라져 「에러 없이 결과가 없다」 로 보였다(2026-10-26 신고). 다음 일을 시작하거나 닫을 때까지 화면에 남긴다
  const [lastError, setLastError] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [renaming, setRenaming] = useState(null);   // 고치는 중인 작업 이름(null = 안 고침)
  const [auto, setAuto] = useState(null);           // 자동 진행 상태 문구
  const stopRef = useRef(false);
  const pendingAutoRef = useRef(null);   // 자동 진행 확인 창을 띄울 작업 id(다른 화면에서 넘어왔을 때)
  const [draft, setDraft] = useState(null);         // 고치는 중인 문서 조각들
  // 지운 AI 대안과 그 사유(선택) — 저장할 때 함께 보낸다. 사유는 「진화 기록」 에 쌓여 다음에 같은 처방을 권할 때 실린다
  const [altDrops, setAltDrops] = useState([]);    // [{ id, title, reason }]
  const [parts, setParts] = useState({ mode: 'actual', monitorReport: false, stats: true, profile: true });
  const [report, setReport] = useState('');
  // 리포트 보기 범위 — 'full' 전체 / 'brief' 축약(주요 진단 결과·개선 방안·개선 결과만, 사용자 요청 2026-10-10)
  const [reportView, setReportView] = useState('full');
  // 진단 탭 — 계획 위 번호와 카드를 잇는다(마우스를 올린 카드의 줄을 강조, 번호를 누르면 그 카드로)
  const [markHot, setMarkHot] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [autoAsk, setAutoAsk] = useState(null);     // 자동 진행 확인 창의 초기 설정(열려 있으면 객체)
  const cardRefs = useRef({});
  const tRef = useRef(t);
  tRef.current = t;

  const reload = useCallback(async () => {
    try { setJobs((await api.listJobs(q)).jobs || []); } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [q]);
  useEffect(() => { if (open) reload(); }, [open, reload]);

  // 튜닝 가이드(이 작업에 실리는 것·검증 순서)와 분류 코드 목록 — 둘 다 서버가 준다
  const [guideInfo, setGuideInfo] = useState(null);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => { if (open) api.getTopics().then(r => setCatalog(r.topics || [])).catch(() => setCatalog([])); }, [open]);
  const loadGuide = useCallback(async (id) => {
    if (!id) { setGuideInfo(null); return null; }
    try { const r = await api.getGuide(id); setGuideInfo(r.guide); return r.guide; } catch (_e) { setGuideInfo(null); return null; }
  }, []);

  const adopt = useCallback((j, probs) => {
    setJob(j);
    setProblems(probs || []);
    setDraft(j ? JSON.parse(JSON.stringify(j.doc)) : null);
    setAltDrops([]);
    // 작업이 바뀌거나 저장될 때마다 다시 읽는다 — 가이드 선택·개선안의 처방 코드가 바뀌면 검증 순서가 달라진다
    loadGuide(j ? j.id : null);
  }, [loadGuide]);
  // 새로고침 뒤 — 보던 작업·단계로 한 번만 되돌린다(App 이 workspaceLayout 에서 준 restore). 채팅·다른 화면이 가리킨 작업(focus·seed)이 있으면 그쪽이 앞선다.
  // 작업이 지워졌으면 조용히 목록만 보인다. 되돌리기가 끝난 뒤에야 위치를 알린다 — 먼저 알리면 「작업 없음」 이 저장된 위치를 덮는다.
  const [viewReady, setViewReady] = useState(false);
  useEffect(() => {
    if (!open || viewReady) return;
    const want = restore && restore.id && !(focus && focus.jobId);
    if (!want) { setViewReady(true); return; }
    let alive = true;
    api.getJob(restore.id)
      .then((r) => { if (!alive) return; adopt(r.job, r.problems); if (restore.step && TABS.includes(restore.step)) setStep(restore.step); })
      .catch(() => { /* 지워진 작업 — 목록만 */ })
      .finally(() => { if (alive) setViewReady(true); });
    return () => { alive = false; };   // eslint-disable-line consistent-return
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 처음 열릴 때 한 번
  }, [open]);
  const viewJobId = job ? job.id : null;
  useEffect(() => { if (open && viewReady && onView) onView({ id: viewJobId, step }); }, [open, viewReady, viewJobId, step, onView]);

  const openJob = async (id) => {
    try { const r = await api.getJob(id); adopt(r.job, r.problems); setCreating(false); setReport(''); return r.job; } catch (err) { toast.error(formatApiError(err, t)); return null; }
  };

  // 채팅 에이전트가 작업을 만들거나 바꿨다(SSE sql_tuning → App → focus, 가이드라인 원칙 6).
  //   open 이면 그 작업·단계로 연다 / 아니면 보고 있는 작업일 때만 새로 읽는다 — **저장하지 않은 고침이 있으면 덮지 않고 알린다**
  const jobRef = useRef(null);
  jobRef.current = { job, draft };
  useEffect(() => {
    if (!open || !focus || !focus.jobId) return;
    const cur = jobRef.current;
    const same = !!cur.job && cur.job.id === focus.jobId;
    if (!focus.open && !same) { reload(); return; }
    const dirty = same && cur.draft && JSON.stringify(cur.draft) !== JSON.stringify(cur.job.doc);
    if (dirty) { toast.info(tRef.current('common:stn.changedElsewhere')); return; }
    // 편집기 실행계획 탭의 「AI 튜닝」 에서 넘어왔다 — 작업이 열리면 「AI 튜닝 자동 진행」 확인 창을 띄운다(시작은 사용자가 누른다).
    // 열기 **전에** 표시해 둔다 — 열린 뒤에 표시하면 작업이 먼저 그려져 놓칠 수 있다
    if (focus.autoRun && aiEnabled) pendingAutoRef.current = focus.jobId;
    openJob(focus.jobId).then((j) => {
      if (j && focus.step && TABS.includes(focus.step)) setStep(focus.step);
      if (!j) pendingAutoRef.current = null;
    });
    reload();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, focus && focus.nonce]);

  const run = async (label, fn) => {
    setBusy(label);
    setBusySince(Date.now());
    setLastError(null);
    try { return await fn(); } catch (err) {
      const d = err && err.response && err.response.data;
      const message = formatApiError(err, t);
      toast.error(message);
      setLastError({ label: label.split(':')[0], message });
      if (d && d.problems) setProblems(d.problems);
      return null;
    } finally { setBusy(null); }
  };

  const save = (patch, opts) => run('save', async () => {
    const r = await api.patchJob(job.id, patch, opts);
    adopt(r.job, r.problems);
    reload();
    toast.success(t('common:stn.saved'), { autoClose: 1200 });
    return r;
  });

  const collect = (pp = parts) => run('collect', async () => {
    const r = await withConfirms(flags => api.collectJob(job.id, {
      plan: { mode: pp.mode, monitorReport: pp.monitorReport }, stats: pp.stats, profile: pp.profile,
    }, flags), confirm, t);
    if (!r) return null;
    adopt(r.job);
    (r.notes || []).forEach(n => toast.info(n));
    const p = r.job.doc.collect.plan;
    if (p && p.monitorError) toast.warning(t('common:stn.monitorFailed', { reason: p.monitorError }));
    return r;
  });

  const verify = (altId) => run(`verify:${altId}`, async () => {
    const r = await withConfirms(flags => api.verifyAlt(job.id, altId, flags), confirm, t);
    if (!r) return null;
    adopt(r.job);
    return r;
  });

  const ai = (stepName, extra = {}) => run(`ai:${stepName}`, async () => {
    const r = await api.aiStep(job.id, stepName, { provider: aiProvider, ...extra });
    adopt(r.job, r.problems);
    if ((r.rejected || []).length) toast.warning(t('common:stn.aiRejected', { n: r.rejected.length, first: r.rejected[0].reason }));
    if (((r.guide && r.guide.dropped) || []).length) toast.warning(t('common:stn.guideDropped', { n: r.guide.dropped.length }));
    return r;
  });

  /** 순서대로 검증 — 가이드의 처방 순서로, 아직 재지 않은 대안을 차례로(인덱스 대안은 건별로 확인을 묻는다). 건너뛰기 기준은 자동 진행만 쓴다 */
  const verifyInOrder = async () => {
    stopRef.current = false;
    const g = guideInfo || (await loadGuide(job.id));
    for (const a of orderedAlts((job.doc.alternatives || []).filter(x => !x.result), g)) {
      if (stopRef.current) return;
      // eslint-disable-next-line no-await-in-loop
      if (!(await verify(a.id))) return;
    }
  };

  const rewrite = async (stepName, instruction) => !!(await ai(stepName, { instruction, ...(stepName === 'alternatives' ? { scope: (draft && draft.scope) || 'query' } : {}) }));

  /**
   * 중지 — DB 에서 도는 측정과 **진행 중인 AI 호출을 함께** 끊는다(2026-10-15 점검 A4: DB 취소만 불러 AI 단계가 계속 돌았다).
   * 자동 진행은 stopRef 를 단계마다 본다. 둘 다 「이미 끝났음」 일 수 있어 실패는 삼킨다.
   */
  const cancel = async () => {
    stopRef.current = true;
    await Promise.all([
      api.cancelJob(job.id).catch(() => null),
      api.aiCancel(job.id).catch(() => null),
    ]);
  };

  /** 자동 진행 — 각 단계를 차례로. 실패하거나 사용자가 멈추면 거기서 선다 */
  /**
   * 자동 진행 — 먼저 확인 창(AutoRunDialog)에서 설정을 보고 고친다(사용자 요구 2026-10-09). 그 설정을 작업에 저장하고 그대로 진행한다.
   */
  useEffect(() => {
    if (job && pendingAutoRef.current === job.id) { pendingAutoRef.current = null; askAutoRun(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 넘어온 작업이 열렸을 때 한 번
  }, [job && job.id]);
  const askAutoRun = () => {
    const doc = job.doc || {};
    setAutoAsk({
      parts: { ...parts },
      scope: (draft && draft.scope) || doc.scope || 'query',
      dataLookup: doc.dataLookup !== false,
      caseRef: !!(doc.caseRef && doc.caseRef.enabled),
      verify: true,
      verifyIndex: job.connType === 'dev',
    });
  };

  const autoRun = async (o) => {
    stopRef.current = false;
    const say = (k) => setAuto(t(`common:stn.auto.${k}`));
    try {
      // 확인 창에서 고른 것을 작업에 남긴다 — 화면의 설정·리포트·다음 단계가 같은 값을 본다
      setParts(o.parts);
      const ref = { ...((job.doc && job.doc.caseRef) || { ids: [] }), enabled: !!o.caseRef };
      if (!(await save({ scope: o.scope, dataLookup: !!o.dataLookup, caseRef: ref }, { allowDraft: true }))) return;
      say('collect'); setStep('collect');
      if (!(await collect(o.parts))) return;
      if (stopRef.current) return;
      say('diagnose'); setStep('diagnose');
      if (!(await ai('diagnose'))) return;
      if (stopRef.current) return;
      say('alternatives'); setStep('alternatives');
      // 자동 진행은 처음부터 다시 쓰는 것이다 — 앞 실행분 AI 대안이 쌓이지 않게 갈아 끼운다(사람이 쓴 것·검증한 것은 남는다)
      const r = await ai('alternatives', { scope: o.scope, replace: true });
      if (!r) return;
      // 검증하지 않으면(끔 · 예상 계획이라 기준 실측이 없음) 개선안까지만 — 해석·최종안은 검증 결과를 읽는 단계다
      if (!o.verify || o.parts.mode !== 'actual') { toast.info(t('common:stn.ar.stoppedAfterAlt')); return; }
      // **가이드의 처방 순서대로** 잰다(순서는 서버가 정한다 — 사람의 「순서대로 검증」 과 같다). 앞선 대안이 건너뛰기 기준을 채우면 뒤는 재지 않는다
      const g = await loadGuide(r.job.id);
      const todo = orderedAlts(r.job.doc.alternatives.filter(x => !x.result), g);
      for (let k = 0; k < todo.length; k += 1) {
        const a = todo[k];
        if (stopRef.current) return;
        if ((a.indexes || []).length && !o.verifyIndex) continue;      // 인덱스 대안은 고른 경우에만(개발 연결·건별 확인)
        setAuto(t('common:stn.auto.verify', { id: a.id }));
        // eslint-disable-next-line no-await-in-loop
        const vr = await verify(a.id);
        if (vr && vr.guideReached && k < todo.length - 1) {
          toast.info(t('common:stn.guide.skipped', { id: a.id, rest: todo.slice(k + 1).map(x => x.id).join(', ') }));
          break;
        }
      }
      // 뒤 단계는 앞 단계가 **성공하고 멈추지 않았을 때만** — 실패하거나 멈췄는데 최종안·리포트가 이어지면 안 된다(점검 A4)
      if (stopRef.current) return;
      say('interpret');
      if (!(await ai('interpret')) || stopRef.current) return;
      say('final'); setStep('final');
      if (!(await ai('final')) || stopRef.current) return;
      say('report'); setStep('report');
      if (!(await loadReport()) || stopRef.current) return;
      toast.success(t('common:stn.auto.done'));
    } finally {
      if (stopRef.current) toast.info(t('common:stn.auto.stopped'));
      setAuto(null);
    }
  };

  const loadReport = (view = reportView) => run('report', async () => {
    const r = await api.getReport(job.id, view);
    setReport(r.markdown || '');
    return r;
  });
  const jobId = job ? job.id : null;
  // 리포트 단계를 열 때마다(또는 작업이 바뀌면) 다시 만든다 — loadReport 는 매 렌더 새 함수라 의존성에서 뺀다
  useEffect(() => { if (step === 'report' && jobId) loadReport(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, jobId, reportView]);

  // 가드레일 4 — 암호화되지 않은 파일로 나가므로 금지 항목이 있으면 빼기·치환·그대로를 묻는다(utils/guardrailGate)
  const downloadReport = async () => {
    const g = await guardExport(report, { scan: guardrailApi.scanText, redact: guardrailApi.redactText });
    if (!g) return;
    if (g.mode === 'unchecked') toast.warning(t('common:grd.exportUnchecked'));
    const blob = new Blob([g.text], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    // 내려받는 것은 지금 보이는 범위다 — 축약이면 이름에 -summary
    a.download = `sql-tuning-${(job.title || job.id).replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 60)}${reportView === 'brief' ? '-summary' : ''}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  // 튜닝 결과를 사례로 — 기계가 옮겨 적은 양식으로 등록 화면을 연다(저장·변조 검증은 거기서 사람이). 저장되면 App 이 작업에 잇는다
  const registerCase = () => run('caseSeed', async () => {
    const r = await api.caseSeed(job.id);
    if (!r.seed.afterSql && !r.seed.indexDdl) toast.info(t('common:stn.registerCaseNoBest'));
    onRegisterCase(r.seed);
    return r;
  });

  const rename = async () => {
    const title = String(renaming || '').trim();
    setRenaming(null);
    if (!title || title === job.title) return;
    await run('save', async () => {
      const r = await api.patchJob(job.id, {}, { title, allowDraft: true });
      setJob(r.job);
      setDraft(prev => (prev ? { ...prev, history: r.job.doc.history } : prev));
      reload();
      return r;
    });
  };

  const removeJob = async () => {
    const ok = await confirm({ title: t('common:stn.deleteTitle'), message: t('common:stn.deleteBody', { title: job.title }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    await run('delete', async () => { await api.deleteJob(job.id); adopt(null); reload(); });
  };

  const d = draft;
  const plan = job && job.doc.collect && job.doc.collect.plan;
  const verified = d ? (d.alternatives || []).filter(a => a.result && a.result.verdict !== 'invalid') : [];
  const running = !!busy || !!auto;

  // 머리줄 「저장」 이 지금 단계에서 저장하는 칸 — 단계마다 맨 아래 있던 저장 단추를 대신한다(2026-10-26)
  const STEP_SAVE = {
    target: { fields: ['target'], patch: () => ({ target: d.target }) },
    collect: { fields: ['collect'], patch: () => ({ collect: { notes: d.collect.notes } }) },
    diagnose: { fields: ['diagnosis'], patch: () => ({ diagnosis: d.diagnosis }) },
    alternatives: { fields: ['scope', 'alternatives', 'interpretation', 'caseRef'], patch: () => ({ scope: d.scope, alternatives: d.alternatives, interpretation: d.interpretation, caseRef: d.caseRef || { enabled: false, ids: [] } }), opts: () => ({ dropReasons: dropReasonPayload(altDrops) }) },
    final: { fields: ['final'], patch: () => ({ final: d.final }) },
  };
  const stepSave = job && d && !job.readOnly ? STEP_SAVE[step] : null;
  const stepIsDirty = !!stepSave && (stepDirty(d, job.doc, stepSave.fields) || (step === 'alternatives' && altDrops.length > 0));
  const saveStep = () => { if (stepSave) save(stepSave.patch(), stepSave.opts ? stepSave.opts() : undefined); };


  const openCompare = (alt) => {
    if (!onOpenPlans || !plan || !alt.result) return;
    onOpenPlans({
      title: `${job.title} · ${alt.id}`, connectionId: job.connectionId, nonce: `${job.id}_${alt.id}_${Date.now()}`,
      answer: { planText: plan.planText, label: t('common:stn.before'), sql: job.doc.target.sql },
      submitted: { planText: alt.result.planText, label: `${alt.id} ${alt.title || ''}`, sql: alt.sql || job.doc.target.sql },
    });
  };

  const aiBtn = (stepName, label, extra) => (aiEnabled ? (
    <button type="button" className="app-btn stn-ai" onClick={() => ai(stepName, extra)} disabled={running}>
      <FiCpu size={12} /> {busy === `ai:${stepName}` ? t('common:stn.aiWorking') : label}
    </button>
  ) : null);

  // ── 단계별 화면 ─────────────────────────────────────────────
  const renderTarget = () => (
    <div className="stn-step">
      <div className="stn-row stn-between">
        <span className="stn-label">{t('common:stn.sql')}{job.doc.target.sqlId ? ` · sql_id ${job.doc.target.sqlId}` : ''}</span>
        <span className="stn-note">{t('common:stn.targetChangeNote')}</span>
      </div>
      <textarea className="stn-sql stn-sql-tall" rows={18} value={d.target.sql} spellCheck={false}
        onChange={e => { const sql = e.target.value; const names = detectBindVariables(sql); setDraft({ ...d, target: { ...d.target, sql, binds: names.map(n => d.target.binds.find(b => b.name === n) || { name: n, value: '', dataType: '' }) } }); }} />
      <BindEditor binds={d.target.binds} onChange={binds => setDraft({ ...d, target: { ...d.target, binds } })} />
    </div>
  );

  const renderCollect = () => {
    const st = job.doc.collect.stats;
    const prof = job.doc.collect.profile;
    return (
      <div className="stn-step">
        <div className="stn-box">
          <div className="stn-row">
            <label><input type="radio" checked={parts.mode === 'actual'} onChange={() => setParts(p => ({ ...p, mode: 'actual' }))} /> {t('common:stn.modeActual')}</label>
            <label><input type="radio" checked={parts.mode === 'estimated'} onChange={() => setParts(p => ({ ...p, mode: 'estimated' }))} /> {t('common:stn.modeEstimated')}</label>
            <label title={t('common:stn.monitorHint')}><input type="checkbox" checked={parts.monitorReport} disabled={parts.mode !== 'actual'} onChange={e => setParts(p => ({ ...p, monitorReport: e.target.checked }))} /> {t('common:stn.monitorReport')}</label>
          </div>
          <div className="stn-row">
            <label><input type="checkbox" checked={parts.stats} onChange={e => setParts(p => ({ ...p, stats: e.target.checked }))} /> {t('common:stn.statsIdx')}</label>
            <label title={t('common:stn.profileHint')}><input type="checkbox" checked={parts.profile} onChange={e => setParts(p => ({ ...p, profile: e.target.checked }))} /> {t('common:stn.profile')}</label>
            <span className="stn-grow" />
            {busy === 'collect'
              ? <button type="button" className="app-btn app-btn-danger" onClick={cancel}><FiSquare size={12} /> {t('common:stn.stop')}</button>
              : <button type="button" className="app-btn app-btn-primary" onClick={() => collect()} disabled={running}><FiPlay size={12} /> {t('common:stn.collect')}</button>}
          </div>
          {parts.mode === 'actual' && <div className="stn-note">{t('common:stn.actualNote')}</div>}
        </div>
        {plan ? (
          <div className="stn-box stn-fade">
            <div className="stn-row stn-between">
              <b>{t('common:stn.plan')} ({plan.mode === 'actual' ? t('common:stn.modeActual') : t('common:stn.modeEstimated')})</b>
              {onOpenPlans && <button type="button" className="app-btn" onClick={() => onOpenPlans({ title: job.title, connectionId: job.connectionId, nonce: `${job.id}_base_${Date.now()}`, answer: { planText: plan.planText, label: t('common:stn.before'), sql: job.doc.target.sql } })}><FiExternalLink size={12} /> {t('common:stn.openPlanTab')}</button>}
            </div>
            <Metrics m={plan.metrics} />
            {(plan.symptoms || []).length > 0 && <div className="stn-chips">{plan.symptoms.map(s => <span key={s.id} className="stn-chip" title={s.why}>{t(`assistant:planInsights.sym.${s.id}`, { defaultValue: s.label })}</span>)}</div>}
            {plan.sampleError && <div className="stn-note">{t('common:stn.sampleFailed', { reason: plan.sampleError })}</div>}
            {(plan.notes || []).map(n => <div key={n} className="stn-warn-line">{n}</div>)}
            {/* 한글 객체 이름 줄이 밀리지 않게 넓은 글자를 2칸 상자에(utils/monoWide — 실행계획 탭과 같은 방식) */}
            <pre className="stn-pre">{monoWideText(plan.planText)}</pre>
            {plan.monitorReport && <details><summary>{t('common:stn.monitorReport')}</summary><pre className="stn-pre">{monoWideText(plan.monitorReport)}</pre></details>}
          </div>
        ) : <div className="stn-note">{t('common:stn.noPlan')}</div>}
        <div className="stn-box">
          <div className="stn-row"><b>{t('common:stn.tables')}</b>
            <span className="stn-note">{(job.doc.collect.tables || []).map(x => `${x.owner}.${x.table}`).join(', ') || t('common:stn.tablesAuto')}</span></div>
          {st && Object.keys(st.tables || {}).length > 0 && (
            <div className="stn-table-wrap">
              <table className="stn-table">
                <thead><tr><th>{t('common:stn.table')}</th><th className="num">{t('common:stn.numRows')}</th><th className="num">{t('common:stn.blocks')}</th><th>{t('common:stn.analyzed')}</th><th>{t('common:stn.indexes')}</th></tr></thead>
                <tbody>
                  {Object.entries(st.tables).map(([k, v]) => (
                    <tr key={k}><td className="mono">{k}</td><td className="num">{fmt(v.table && v.table.numRows)}</td><td className="num">{fmt(v.table && v.table.blocks)}</td>
                      <td>{(v.table && v.table.lastAnalyzed) || t('common:stn.noStats')}</td>
                      <td className="mono stn-small">{(v.indexes || []).map(i => `${i.name}(${(i.columns || []).join(',')})`).join(' · ') || '-'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {st && (st.note || []).map(n => <div key={n} className="stn-note">{n}</div>)}
          {prof && (
            <div className="stn-small">
              {(prof.dists || []).map(x => <div key={`${x.table}.${x.column}`} className="mono">{x.table}.{x.column}: {(x.periods || []).map(p => `${p.p} ${fmt(p.rows)}`).slice(0, 24).join(' · ')}</div>)}
              {/* 조건에서 함수로 가공된 컬럼의 값 형식 — 모양만(숫자 9·영문 A·그 밖 X). 가공 없이 쓸 수 있는 조건을 찾는 근거 */}
              {(prof.patterns || []).length > 0 && <div className="stn-note">{t('common:stn.patternsHint')}</div>}
              {(prof.patterns || []).map(x => (
                <div key={`p-${x.table}.${x.column}`} className="mono">
                  {x.table}.{x.column} ({x.fn}): {(x.patterns || []).map(p => `${p.p} ${p.pct != null ? `${p.pct}%` : fmt(p.n)}`).join(' · ')}
                  {x.sampledPct ? ` ${t('common:stn.sampled', { pct: x.sampledPct })}` : ''}
                </div>
              ))}
              {(prof.note || []).map(n => <div key={n} className="stn-note">{n}</div>)}
            </div>
          )}
        </div>
        <div className="stn-box">
          <b>{t('common:stn.notes')}</b>
          <textarea rows={3} value={d.collect.notes || ''} placeholder={t('common:stn.notesPh')} onChange={e => setDraft({ ...d, collect: { ...d.collect, notes: e.target.value } })} />
        </div>
      </div>
    );
  };

  const setItem = (i, k, v) => setDraft({ ...d, diagnosis: { ...d.diagnosis, items: d.diagnosis.items.map((x, j) => (j === i ? { ...x, [k]: v, origin: 'user' } : x)) } });
  const goCard = (label) => {
    setMarkHot(label);
    const el = cardRefs.current[label];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };
  const renderDiagnose = () => {
    const items = d.diagnosis.items || [];
    // 계획 줄 Id → 그 줄을 짚은 번호들
    const marks = new Map();
    items.forEach((it, i) => planIdsOf(it).forEach(id => marks.set(id, [...(marks.get(id) || []), itemLabel(it, i)])));
    // 참고 사항(성능 영향 없음)은 회색 번호 N1… 로 — 개선안이 겨냥하지 않는다
    const notes = d.diagnosis.notes || [];
    notes.forEach((n, i) => planIdsOf(n).forEach(id => marks.set(id, [...(marks.get(id) || []), `N${i + 1}`])));
    const setNotes = (list) => setDraft({ ...d, diagnosis: { ...d.diagnosis, notes: list } });
    return (
      <div className="stn-step">
        {/* 튜닝 가이드 — 진단부터 실린다. 고르면 바로 저장한다(AI 단계가 저장된 설정을 읽는다) */}
        <GuidePicker info={guideInfo} value={d.guideRef} disabled={running} onManage={onOpenGuides}
          onChange={(guideRef) => { setDraft({ ...d, guideRef }); save({ guideRef }); }} />
        <div className="stn-row stn-between">
          <span className="stn-note">{t('common:stn.diagHint')}</span>
          {aiBtn('diagnose', t('common:stn.aiDiagnose'))}
        </div>
        {aiEnabled && items.length > 0 && <RewriteBar options={['diagnose']} onRewrite={rewrite} disabled={running} />}
        {/* 문제를 지적한 줄에 번호를 단 실행계획(사용자 제안 2026-10-08) — 번호를 누르면 그 항목으로 */}
        {plan && plan.planText && (
          <div className="stn-box">
            <div className="stn-row stn-between">
              <b>{t('common:stn.planMarked')}</b>
              <span className="stn-note">{marks.size ? t('common:stn.planMarkedHint') : t('common:stn.planMarkedNone')}</span>
            </div>
            <PlanMarked planText={plan.planText} marks={marks} active={markHot} onPick={goCard} />
          </div>
        )}
        <textarea rows={2} value={d.diagnosis.summary || ''} placeholder={t('common:stn.summaryPh')} onChange={e => setDraft({ ...d, diagnosis: { ...d.diagnosis, summary: e.target.value } })} />
        {items.map((it, i) => {
          const label = itemLabel(it, i);
          const ids = planIdsOf(it);
          return (
            <div key={it.id || i} ref={(el) => { cardRefs.current[label] = el; }}
              className={`stn-card stn-fade stn-diag${markHot === label ? ' hot' : ''}`}
              onMouseEnter={() => setMarkHot(label)} onMouseLeave={() => setMarkHot(h => (h === label ? null : h))}>
              <div className="stn-row stn-between">
                <span className="stn-row">
                  <span className="stn-pl-badge static">{label}</span>
                  {it.origin === 'ai' && <span className="stn-badge">AI</span>}
                  <label className="stn-planids" title={t('common:stn.planIdsHint')}>
                    {t('common:stn.planIds')}
                    <input className="mono" value={ids.join(', ')} placeholder="4, 7"
                      onChange={e => setItem(i, 'planIds', e.target.value.split(/[\s,]+/).filter(Boolean).map(Number).filter(n => Number.isInteger(n) && n >= 0))} />
                  </label>
                </span>
                <button type="button" className="app-btn app-btn-danger" onClick={() => setDraft({ ...d, diagnosis: { ...d.diagnosis, items: items.filter((_, j) => j !== i) } })} title={t('common:ui.delete')}><FiTrash2 size={11} /></button>
              </div>
              <label className="stn-field"><span>{t('common:stn.point')}</span><input value={it.point || ''} onChange={e => setItem(i, 'point', e.target.value)} /></label>
              <div className="stn-grid2">
                <label className="stn-field"><span>{t('common:stn.symptom')}</span><textarea rows={3} value={it.symptom || ''} onChange={e => setItem(i, 'symptom', e.target.value)} /></label>
                <label className="stn-field"><span>{t('common:stn.evidence')}</span><textarea rows={3} value={it.evidence || ''} onChange={e => setItem(i, 'evidence', e.target.value)} /></label>
              </div>
              <label className="stn-field"><span>{t('common:stn.cause')}</span><textarea rows={2} value={it.cause || ''} onChange={e => setItem(i, 'cause', e.target.value)} /></label>
              {/* 분류 코드(선택) — 튜닝 사례와 같은 낱말. 사례 참조가 이것으로 원인이 같은 사례를 먼저 찾는다 */}
              <div className="stn-row stn-codeRow">
                <CodeChips label={t('common:stn.codes.symptom')} axis="symptom" codes={it.symptomCodes} catalog={catalog} onChange={v => setItem(i, 'symptomCodes', v)} />
                <CodeChips label={t('common:stn.codes.cause')} axis="cause" codes={it.causeCodes} catalog={catalog} onChange={v => setItem(i, 'causeCodes', v)} />
              </div>
            </div>
          );
        })}
        {/* 참고 사항 — 쿼리 로직의 지적이지만 성능에 영향이 없는 것(개선안 작성 가이드 1). 개선안이 겨냥하지 않는다 */}
        <div className="stn-box stn-notes">
          <div className="stn-row stn-between"><b>{t('common:stn.notesTitle')}</b><span className="stn-note">{t('common:stn.notesHintDiag')}</span></div>
          {notes.map((n, i) => (
            // eslint-disable-next-line react/no-array-index-key -- 참고 사항은 번호가 없다(순서가 곧 N 번호)
            <div key={i} ref={(el) => { cardRefs.current[`N${i + 1}`] = el; }} className={`stn-row stn-note-item${markHot === `N${i + 1}` ? ' hot' : ''}`}
              onMouseEnter={() => setMarkHot(`N${i + 1}`)} onMouseLeave={() => setMarkHot(h => (h === `N${i + 1}` ? null : h))}>
              <span className="stn-pl-badge static note">{`N${i + 1}`}</span>
              {n.origin === 'ai' && <span className="stn-badge">AI</span>}
              <input className="mono stn-note-ids" value={planIdsOf(n).join(', ')} placeholder={t('common:stn.planIds')} title={t('common:stn.planIdsHint')}
                onChange={e => setNotes(notes.map((x, j) => (j === i ? { ...x, planIds: e.target.value.split(/[\s,]+/).filter(Boolean).map(Number).filter(v => Number.isInteger(v) && v >= 0), origin: 'user' } : x)))} />
              <textarea className="stn-grow" rows={2} value={n.text || ''} onChange={e => setNotes(notes.map((x, j) => (j === i ? { ...x, text: e.target.value, origin: 'user' } : x)))} />
              <button type="button" className="app-btn app-btn-danger" onClick={() => setNotes(notes.filter((_, j) => j !== i))} title={t('common:ui.delete')}><FiTrash2 size={11} /></button>
            </div>
          ))}
          <div><button type="button" className="app-btn" onClick={() => setNotes([...notes, { text: '', planIds: [], origin: 'user' }])}><FiPlus size={12} /> {t('common:stn.addNote')}</button></div>
        </div>
        <div className="stn-actions">
          <button type="button" className="app-btn" onClick={() => setDraft({ ...d, diagnosis: { ...d.diagnosis, items: [...items, { planIds: [], point: '', symptom: '', cause: '', evidence: '', origin: 'user' }] } })}><FiPlus size={12} /> {t('common:stn.addItem')}</button>
        </div>
      </div>
    );
  };

  const setAlt = (i, k, v) => setDraft({ ...d, alternatives: d.alternatives.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
  const verdictCls = (v) => `stn-verdict v-${v}`;
  const renderAlternatives = () => {
    const dIds = (job.doc.diagnosis.items || []).map(x => x.id);
    const tables = (job.doc.collect.tables || []).map(x => `${x.owner}.${x.table}`);
    const interp = new Map(((d.interpretation && d.interpretation.items) || []).map(x => [x.alt, x]));
    return (
      <div className="stn-step">
        {aiEnabled && (
          <CaseRefBox jobId={job.id} value={d.caseRef} onChange={caseRef => setDraft({ ...d, caseRef })}
            disabled={running} catalog={catalog} />
        )}
        <div className="stn-row">
          <span className="stn-label">{t('common:stn.scope')}</span>
          {SCOPES.map(s => <label key={s}><input type="radio" checked={d.scope === s} onChange={() => setDraft({ ...d, scope: s })} /> {t(`common:stn.scopes.${s}`)}</label>)}
          <span className="stn-grow" />
          {aiBtn('alternatives', t('common:stn.aiAlternatives'), { scope: d.scope })}
          {aiBtn('interpret', t('common:stn.aiInterpret'))}
        </div>
        {aiEnabled && (
          <RewriteBar
            options={[(job.doc.alternatives || []).length ? 'alternatives' : null, ((job.doc.interpretation || {}).items || []).length ? 'interpret' : null].filter(Boolean)}
            onRewrite={rewrite} disabled={running} />
        )}
        {/* AI 개선안 요청 때 서버가 원래 조건으로 실 데이터(저장값)를 조회해 AI 에게 함께 보낸다(사용자 요구 2026-10-08) — 기본 켬 */}
        {aiEnabled && (
          <label className="stn-row stn-note" title={t('common:stn.dataLookupHint')}>
            <input type="checkbox" checked={d.dataLookup !== false}
              onChange={(e) => { const v = e.target.checked; setDraft({ ...d, dataLookup: v }); save({ dataLookup: v }); }} disabled={running} />
            {t('common:stn.dataLookup')}
          </label>
        )}
        {/* 검증 순서 — 가이드의 처방 순서대로(서버가 정한다). 저장된 개선안 기준이라 고친 뒤에는 저장해야 바뀐다 */}
        {guideInfo && (guideInfo.order || []).length > 1 && (
          <div className="stn-row stn-note stn-order">
            <span>{guideInfo.priority
              ? t('common:stn.guide.order', { from: (guideInfo.priorityFrom && guideInfo.priorityFrom.title) || '-', order: guideInfo.order.map(x => x.id).join(' → ') })
              : t('common:stn.guide.orderNone')}</span>
            <span className="stn-grow" />
            <button type="button" className="app-btn" onClick={verifyInOrder} disabled={running || !plan || plan.mode !== 'actual' || !(job.doc.alternatives || []).some(x => !x.result)}
              title={!plan || plan.mode !== 'actual' ? t('common:stn.needActual') : t('common:stn.guide.verifyOrderHint')}><FiPlay size={12} /> {t('common:stn.guide.verifyOrder')}</button>
          </div>
        )}
        {(d.alternatives || []).map((a, i) => {
          const saved = (job.doc.alternatives || []).find(x => x.id === a.id);
          const r = saved && saved.result;
          return (
            <div key={a.id || i} className="stn-card stn-fade">
              <div className="stn-row stn-between">
                <span className="stn-row">
                  <b>{a.id || t('common:stn.newAlt')}</b>
                  {a.origin === 'ai' && <span className="stn-badge">AI</span>}
                  {/* 비중 — 부수 개선은 주요 개선 뒤에 놓인다(저장할 때 서버가 정렬) */}
                  <select className={`stn-impact${a.impact === 'minor' ? ' minor' : ''}`} value={a.impact || ''} onChange={e => setAlt(i, 'impact', e.target.value)}
                    title={t('common:stn.impactHint')} aria-label={t('common:stn.impact')}>
                    <option value="">{t('common:stn.impacts.none')}</option>
                    <option value="major">{t('common:stn.impacts.major')}</option>
                    <option value="minor">{t('common:stn.impacts.minor')}</option>
                  </select>
                </span>
                <span className="stn-row">
                  {saved && (busy === `verify:${a.id}`
                    ? <button type="button" className="app-btn app-btn-danger" onClick={cancel}><FiSquare size={12} /> {t('common:stn.stop')}</button>
                    : <button type="button" className="app-btn app-btn-primary" onClick={() => verify(a.id)} disabled={running || !plan || plan.mode !== 'actual'} title={!plan || plan.mode !== 'actual' ? t('common:stn.needActual') : ''}><FiPlay size={12} /> {t('common:stn.verify')}</button>)}
                  <button type="button" className="app-btn app-btn-danger" title={t('common:ui.delete')}
                    onClick={() => {
                      setDraft({ ...d, alternatives: d.alternatives.filter((_, j) => j !== i) });
                      // 저장돼 있던 AI 대안을 지울 때만 사유를 물을 자리를 낸다(선택 — 고르지 않아도 저장된다)
                      if (saved && saved.origin === 'ai') setAltDrops(list => [...list.filter(x => x.id !== a.id), { id: a.id, title: saved.title || '', reason: '' }]);
                    }}><FiTrash2 size={11} /></button>
                </span>
              </div>
              <label className="stn-field"><span>{t('common:stn.altTitle')}</span><input value={a.title || ''} onChange={e => setAlt(i, 'title', e.target.value)} /></label>
              <div className="stn-row"><span className="stn-label">{t('common:stn.targets')}</span>
                {dIds.map(id => <label key={id}><input type="checkbox" checked={(a.targets || []).includes(id)} onChange={e => setAlt(i, 'targets', e.target.checked ? [...(a.targets || []), id] : (a.targets || []).filter(x => x !== id))} /> {id}</label>)}
                {!dIds.length && <span className="stn-note">{t('common:stn.noDiag')}</span>}
              </div>
              {/* 처방 분류(선택) — 가이드의 처방 순서(검증 순서)와 사례 등록 제안이 읽는다 */}
              <div className="stn-row stn-codeRow">
                <CodeChips label={t('common:stn.codes.fix')} axis="fix" codes={a.fixCodes} catalog={catalog} onChange={v => setAlt(i, 'fixCodes', v)} />
              </div>
              <label className="stn-field"><span>{t('common:stn.altSql')}</span>
                <textarea className="stn-sql" rows={6} value={a.sql || ''} placeholder={t('common:stn.altSqlPh')} spellCheck={false} onChange={e => setAlt(i, 'sql', e.target.value)} /></label>
              {d.scope !== 'query' && (
                <div className="stn-box">
                  <span className="stn-label">{t('common:stn.altIndexes')}</span>
                  {(a.indexes || []).map((ix, k) => (
                    <div key={k} className="stn-row">
                      <select value={ix.table} onChange={e => setAlt(i, 'indexes', a.indexes.map((x, m) => (m === k ? { ...x, table: e.target.value } : x)))}>
                        {[ix.table, ...tables.filter(x => x !== ix.table)].filter(Boolean).map(x => <option key={x} value={x}>{x}</option>)}
                      </select>
                      <input className="stn-grow mono" value={(ix.columns || []).join(', ')} placeholder={t('common:stn.columnsPh')} onChange={e => setAlt(i, 'indexes', a.indexes.map((x, m) => (m === k ? { ...x, columns: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } : x)))} />
                      <label><input type="checkbox" checked={!!ix.unique} onChange={e => setAlt(i, 'indexes', a.indexes.map((x, m) => (m === k ? { ...x, unique: e.target.checked } : x)))} /> UNIQUE</label>
                      <button type="button" className="app-btn" onClick={() => setAlt(i, 'indexes', a.indexes.filter((_, m) => m !== k))}><FiTrash2 size={11} /></button>
                    </div>
                  ))}
                  {tables.length > 0 && (a.indexes || []).length < 4 && <button type="button" className="app-btn" onClick={() => setAlt(i, 'indexes', [...(a.indexes || []), { table: tables[0], columns: [], unique: false }])}><FiPlus size={11} /> {t('common:stn.addIndex')}</button>}
                  {job.connType !== 'dev' && <div className="stn-note">{t('common:stn.indexDevOnly')}</div>}
                </div>
              )}
              <label className="stn-field"><span>{t('common:stn.rationale')}</span><textarea rows={2} value={a.rationale || ''} onChange={e => setAlt(i, 'rationale', e.target.value)} /></label>
              {/* 전제 — 이 대안이 기대는 데이터 가정. 검증(기계)은 결과가 같은지 보지만 표본 밖까지 보장하지 않는다 */}
              <label className="stn-field" title={t('common:stn.assumptionHint')}><span>{t('common:stn.assumption')}</span>
                <input value={a.assumption || ''} placeholder={t('common:stn.assumptionPh')} onChange={e => setAlt(i, 'assumption', e.target.value)} /></label>
              {/* 가이드와 다르게 한 점 — 실린 튜닝 가이드의 기준을 따르지 않았을 때만. AI 가 적었거나 이미 적혀 있을 때 보인다(빈 칸을 늘 세우지 않는다) */}
              {(a.guideNote || (saved && saved.guideNote)) ? (
                <label className="stn-field" title={t('common:stn.guideNoteHint')}><span>{t('common:stn.guideNote')}</span>
                  <input value={a.guideNote || ''} onChange={e => setAlt(i, 'guideNote', e.target.value)} /></label>
              ) : null}
              {r && (
                <div className="stn-result stn-fade">
                  <div className="stn-row">
                    <span className={verdictCls(r.verdict)}>{t(`common:stn.verdicts.${r.verdict}`, { defaultValue: r.verdict })}</span>
                    <span>{t('common:stn.m.buffers')} {fmt(r.before && r.before.buffers)} → <b>{fmt(r.after && r.after.buffers)}</b>{r.deltaPct && r.deltaPct.buffers != null ? ` (${r.deltaPct.buffers}%)` : ''}</span>
                    <span>{t('common:stn.sameResult')}: {r.sameResult === true ? t('common:stn.yes') : r.sameResult === false ? t('common:stn.no') : t('common:stn.unknown')}</span>
                    {onOpenPlans && <button type="button" className="app-btn" onClick={() => openCompare(saved)}><FiExternalLink size={12} /> {t('common:stn.openCompare')}</button>}
                  </div>
                  {r.reason && <div className="stn-small">{r.reason}</div>}
                  {(r.warnings || []).map(w => <div key={w} className="stn-warn">{w}</div>)}
                  <label className="stn-field"><span>{t('common:stn.interpretation')}{interp.get(a.id) && interp.get(a.id).origin === 'ai' ? ' (AI)' : ''}</span>
                    <textarea rows={2} value={(interp.get(a.id) && interp.get(a.id).text) || ''} onChange={e => {
                      const items = ((d.interpretation && d.interpretation.items) || []).filter(x => x.alt !== a.id);
                      setDraft({ ...d, interpretation: { ...(d.interpretation || {}), items: e.target.value ? [...items, { alt: a.id, text: e.target.value, origin: 'user' }] : items } });
                    }} /></label>
                </div>
              )}
            </div>
          );
        })}
        {altDrops.length > 0 && (
          <div className="stn-drops stn-fade">
            <div className="stn-note">{t('common:stn.dropAsk')}</div>
            {altDrops.map(x => (
              <div key={x.id} className="stn-row stn-drop">
                <span className="stn-drop-name">{x.id}{x.title ? ` — ${x.title}` : ''}</span>
                <span className="stn-chips">
                  {ALT_DROP_REASONS.map(rs => (
                    <button key={rs} type="button" className={`stn-drop-chip${x.reason === rs ? ' on' : ''}`} aria-pressed={x.reason === rs}
                      onClick={() => setAltDrops(list => list.map(y => (y.id === x.id ? { ...y, reason: y.reason === rs ? '' : rs } : y)))}>{t(`common:stn.dropReasons.${rs}`)}</button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="stn-actions">
          <button type="button" className="app-btn" onClick={() => setDraft({ ...d, alternatives: [...(d.alternatives || []), { title: '', sql: job.doc.target.sql, indexes: [], targets: [], rationale: '', origin: 'user' }] })}><FiPlus size={12} /> {t('common:stn.addAlt')}</button>
        </div>
        <div className="stn-note">{t('common:stn.verifyNote')}</div>
      </div>
    );
  };

  const ranking = (d && d.final && d.final.ranking) || [];
  const setRanking = (rk) => setDraft({ ...d, final: { ...d.final, ranking: rk } });
  const renderFinal = () => (
    <div className="stn-step">
      <div className="stn-row stn-between"><span className="stn-note">{t('common:stn.finalHint')}</span>{aiBtn('final', t('common:stn.aiFinal'))}</div>
      {aiEnabled && (((job.doc.final || {}).ranking || []).length > 0 || !!(job.doc.final || {}).summary) && <RewriteBar options={['final']} onRewrite={rewrite} disabled={running} />}
      <textarea rows={2} value={d.final.summary || ''} placeholder={t('common:stn.summaryPh')} onChange={e => setDraft({ ...d, final: { ...d.final, summary: e.target.value } })} />
      {ranking.map((r, i) => {
        const a = (job.doc.alternatives || []).find(x => x.id === r.alt);
        return (
          <div key={r.alt} className="stn-card stn-fade">
            <div className="stn-row stn-between">
              <b>{i + 1}. {r.alt} {a && a.title}</b>
              <span className="stn-row">
                {a && a.result && <span className={verdictCls(a.result.verdict)}>{t(`common:stn.verdicts.${a.result.verdict}`, { defaultValue: a.result.verdict })} · {fmt(a.result.before.buffers)} → {fmt(a.result.after.buffers)}</span>}
                <button type="button" className="app-btn" disabled={i === 0} onClick={() => { const x = [...ranking]; [x[i - 1], x[i]] = [x[i], x[i - 1]]; setRanking(x); }}><FiArrowUp size={11} /></button>
                <button type="button" className="app-btn" disabled={i === ranking.length - 1} onClick={() => { const x = [...ranking]; [x[i + 1], x[i]] = [x[i], x[i + 1]]; setRanking(x); }}><FiArrowDown size={11} /></button>
                <button type="button" className="app-btn app-btn-danger" onClick={() => setRanking(ranking.filter((_, j) => j !== i))}><FiTrash2 size={11} /></button>
              </span>
            </div>
            <textarea rows={2} value={r.reason || ''} placeholder={t('common:stn.reasonPh')} onChange={e => setRanking(ranking.map((x, j) => (j === i ? { ...x, reason: e.target.value } : x)))} />
          </div>
        );
      })}
      <div className="stn-row">
        <select value="" onChange={e => { if (e.target.value) setRanking([...ranking, { alt: e.target.value, reason: '' }]); }} aria-label={t('common:stn.addRank')}>
          <option value="">{t('common:stn.addRank')}</option>
          {verified.filter(a => !ranking.some(r => r.alt === a.id)).map(a => <option key={a.id} value={a.id}>{a.id} {a.title}</option>)}
        </select>
        {!verified.length && <span className="stn-note">{t('common:stn.noVerified')}</span>}
      </div>
    </div>
  );

  // 단추 줄(범위·다시 만들기·사례 등록·내려받기)은 ReportView 의 고정 머리에 함께 붙는다 — 긴 리포트를 내려도 남는다
  const renderReport = () => (
    <div className="stn-step">
      {/* 문서로 보기(기본) / 원문 — 내려받는 파일은 .md 그대로 */}
      <ReportView
        markdown={report}
        loadingText={t('common:stn.loading')}
        toolbar={(
          <>
            <div className="stn-row stn-between">
              <span className="stn-note">{t('common:stn.reportHint')}</span>
              <span className="stn-row">
                {/* 전체 / 축약 — 바꾸면 그 범위로 다시 만든다(effect 가 reportView 를 본다) */}
                <span className="stn-row stn-report-mode" role="group" aria-label={t('common:stn.reportScope')}>
                  <button type="button" className={`app-btn${reportView === 'full' ? ' on' : ''}`} aria-pressed={reportView === 'full'} onClick={() => setReportView('full')} disabled={running}>{t('common:stn.reportFull')}</button>
                  <button type="button" className={`app-btn${reportView === 'brief' ? ' on' : ''}`} aria-pressed={reportView === 'brief'} onClick={() => setReportView('brief')} disabled={running} title={t('common:stn.reportBriefHint')}>{t('common:stn.reportBrief')}</button>
                </span>
                <button type="button" className="app-btn" onClick={() => loadReport()} disabled={running}><FiRefreshCw size={12} /> {t('common:stn.refresh')}</button>
                {onRegisterCase && (
                  <button type="button" className="app-btn" onClick={registerCase} disabled={running} title={t('common:stn.registerCaseHint')}>
                    <FiBookOpen size={12} /> {t('common:stn.registerCase')}
                  </button>
                )}
                <button type="button" className="app-btn app-btn-primary" onClick={downloadReport} disabled={!report}><FiDownload size={12} /> {t('common:stn.download')}</button>
              </span>
            </div>
            {(job.doc.cases || []).length > 0 && (
              <div className="stn-note">{t('common:stn.registeredCases')}: {job.doc.cases.map(c => `${c.title} (${c.id})`).join(' · ')}</div>
            )}
          </>
        )}
      />
    </div>
  );

  // 과정 이력 — 서버가 쓴다(화면은 읽기만). 최근 것이 위
  const renderHistory = () => {
    const hist = [...(job.doc.history || [])].reverse();
    return (
      <div className="stn-step">
        <span className="stn-note">{t('common:stn.historyHint')}</span>
        <div className="stn-table-wrap stn-history">
          <table className="stn-table">
            <thead><tr><th>{t('common:stn.hist.at')}</th><th>{t('common:stn.hist.step')}</th><th>{t('common:stn.hist.actor')}</th><th>{t('common:stn.hist.action')}</th><th>{t('common:stn.hist.note')}</th></tr></thead>
            <tbody>
              {hist.map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`${h.at}-${i}`}>
                  <td className="mono stn-small">{storedTime(h.at)}</td>
                  <td>{t(`common:stn.steps.${h.step}`, { defaultValue: h.step })}</td>
                  <td><span className={`stn-actor a-${ACTORS[h.actor] || 'user'}`}>{t(`common:stn.actors.${ACTORS[h.actor] || 'user'}`)}{h.via === 'chat' ? ` · ${t('common:stn.viaChat')}` : ''}</span></td>
                  <td>{t(`common:stn.actions.${h.action}`, { defaultValue: h.action })}</td>
                  <td className="stn-small">{h.note}</td>
                </tr>
              ))}
              {!hist.length && <tr><td colSpan={5} className="stn-note">{t('common:stn.noHistory')}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const body = !job || !d ? null : {
    target: renderTarget, collect: renderCollect, diagnose: renderDiagnose, alternatives: renderAlternatives, final: renderFinal, report: renderReport,
    history: renderHistory,
  }[step]();

  return (
    <AppModal windowed open={open} onClose={onClose} title={t('common:stn.windowTitle')} icon={<FiActivity size={14} />} className="app-modal-default stn-modal">
      <div className="stn-body">
        <aside className="stn-side">
          <button type="button" className="app-btn app-btn-primary stn-full" onClick={() => { setFormKey(k => k + 1); setCreating(true); adopt(null); setLastError(null); }}><FiPlus size={12} /> {t('common:stn.newJob')}</button>
          <input className="stn-full" value={q} onChange={e => setQ(e.target.value)} placeholder={t('common:stn.jobSearch')} onKeyDown={e => { if (e.key === 'Enter') reload(); }} />
          <ul className="stn-jobs">
            {jobs.map(j => (
              <li key={j.id} className={job && job.id === j.id ? 'on' : ''} onClick={() => openJob(j.id)}>
                <div className="stn-job-title">{j.title}</div>
                <div className="stn-job-meta">{j.connName || '-'} · {t(`common:stn.status.${j.status}`, { defaultValue: j.status })}</div>
              </li>
            ))}
            {!jobs.length && <li className="stn-empty">{t('common:stn.noJobs')}</li>}
          </ul>
        </aside>
        <section className="stn-main">
          {creating && <NewJobForm key={formKey} connections={connections} defaultConnectionId={defaultConnectionId} getEditorSql={getEditorSql}
            onCreated={(j) => { setCreating(false); adopt(j); setStep('collect'); reload(); }} onCancel={() => setCreating(false)} />}
          {!creating && !job && <div className="stn-empty-main">{t(aiEnabled ? 'common:stn.intro' : 'common:stn.introNoAi')}</div>}
          {!creating && job && d && (
            <>
              <WorkflowHead
                titleSlot={renaming != null ? (
                    <input className="stn-rename" value={renaming} maxLength={200} aria-label={t('common:stn.rename')} autoFocus
                      onChange={e => setRenaming(e.target.value)} onBlur={rename}
                      onKeyDown={e => { if (e.key === 'Enter') rename(); else if (e.key === 'Escape') { e.stopPropagation(); setRenaming(null); } }} />
                  ) : (
                    <>
                      <b title={job.title}>{job.title}</b>
                      {!job.readOnly && <button type="button" className="stn-icon-btn" onClick={() => setRenaming(job.title)} disabled={running} title={t('common:stn.rename')} aria-label={t('common:stn.rename')}><FiEdit2 size={12} /></button>}
                    </>
                  )}
                meta={`${job.connName || ''}${job.connType ? ` (${job.connType})` : ''}`}
                badges={job.readOnly ? <span className="stn-warn">{t('common:stn.readOnly')}</span> : null}
                dirty={stepIsDirty}
                actions={<>
                  {aiEnabled && (auto
                    ? <button type="button" className="app-btn app-btn-danger" onClick={cancel}><FiSquare size={12} /> {t('common:stn.stopAuto')}</button>
                    : <button type="button" className="app-btn stn-ai" onClick={askAutoRun} disabled={running} title={t('common:stn.autoHint')}><FiCpu size={12} /> {t('common:stn.autoRun')}</button>)}
                  {/* 채팅에서 이어 하기 — 에이전트가 같은 작업을 sql_tuning_* 도구로 이어 받는다(가이드라인 원칙 6). 채우기만 하고 보내지 않는다.
                      창은 닫는다(패널 입력을 가리지 않게) — 에이전트가 sql_tuning_open 으로 다시 연다 */}
                  {aiEnabled && onAskAssistant && (
                    <button type="button" className="app-btn stn-ai" disabled={running} title={t('common:stn.askChatHint')}
                      onClick={() => { onAskAssistant(t('common:stn.askChatPrompt', { title: job.title, id: job.id, step: t(`common:stn.steps.${STEPS.includes(step) ? step : 'target'}`) })); onClose(); }}>
                      <FiMessageSquare size={12} /> {t('common:stn.askChat')}
                    </button>
                  )}
                  {aiEnabled && (
                    <button type="button" className="app-btn" onClick={() => setGuideOpen(true)} title={t('common:stn.wg.hint')}>
                      <FiBookOpen size={12} /> {t('common:stn.wg.button')}
                    </button>
                  )}
                  {job.doc.ai && job.doc.ai.calls > 0 && <span className="stn-note">{t('common:stn.aiUsage', { calls: job.doc.ai.calls, tokens: fmt(job.doc.ai.tokens), cost: job.doc.ai.costUsd })}</span>}
                </>}
                onSave={stepSave ? saveStep : null} saveDisabled={running || !stepIsDirty}
                onDelete={removeJob} deleteDisabled={running}
              />
              <WorkflowRibbon tabs={TABS} step={step} onStep={setStep} label={s => t(`common:stn.steps.${s}`)}
                suffix={s => (s === 'history' && (job.doc.history || []).length > 0 ? ` (${job.doc.history.length})` : '')} />
              {auto && <div className="stn-progress">{auto}</div>}
              {busy && busy.startsWith('ai:') && <AiProgress jobId={job.id} step={busy.slice(3)} provider={aiProvider} />}
              {busy && !auto && !busy.startsWith('ai:') && (
                <div className="stn-progress stn-row" role="status">
                  <span>{t(`common:stn.busy.${busy.split(':')[0]}`, { defaultValue: '…' })}</span>
                  <span className="stn-grow" />
                  {busySince > 0 && <Elapsed since={busySince} />}
                </div>
              )}
              {lastError && !busy && (
                <div className="stn-error stn-row" role="alert">
                  <span className="stn-grow">{t('common:stn.failed', { what: t(`common:stn.failedWhat.${lastError.label}`, { defaultValue: lastError.label }), message: lastError.message })}</span>
                  <button type="button" className="stn-icon-btn" onClick={() => setLastError(null)} aria-label={t('common:ui.close')}><FiX size={12} /></button>
                </div>
              )}
              {problems.length > 0 && <div className="stn-problems">{problems.map(p => <div key={p.code + (p.alt || '')} className={p.soft ? 'stn-problem-soft' : ''}>{p.message}</div>)}</div>}
              <div className="stn-step-wrap" key={`${job.id}-${step}`}>{body}</div>
            </>
          )}
        </section>
      </div>
      {aiEnabled && <WritingGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />}
      {aiEnabled && job && (
        <AutoRunDialog open={!!autoAsk} initial={autoAsk} connName={job.connName} connType={job.connType} provider={aiProvider}
          onCancel={() => setAutoAsk(null)} onStart={(o) => { setAutoAsk(null); autoRun(o); }} />
      )}
    </AppModal>
  );
};

SqlTuningModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  connections: PropTypes.arrayOf(PropTypes.object),
  defaultConnectionId: PropTypes.string,
  /** () => { sql, binds } — 편집기 커서 위치(또는 선택) 문장과 편집기에 입력해 둔 바인드 값 */
  getEditorSql: PropTypes.func,
  /** 실행계획 탭으로 열기(App.handleChallengePlans 와 같은 모양 { title, answer, submitted, connectionId, nonce }) */
  onOpenPlans: PropTypes.func,
  aiEnabled: PropTypes.bool,
  aiProvider: PropTypes.string,
  /** (seed) => void — 튜닝 사례 등록 화면을 이 양식으로 연다(App.handleOpenCaseDraft). 없으면 「튜닝 사례로 등록」 을 숨긴다 */
  onRegisterCase: PropTypes.func,
  /** 튜닝 가이드 관리 창을 연다(설계·튜닝 가이드의 튜닝 탭) */
  onOpenGuides: PropTypes.func,
  /** 새로고침 전에 보던 위치 { id, step } — 처음 열릴 때 한 번 되돌린다 */
  restore: PropTypes.shape({ id: PropTypes.string, step: PropTypes.string }),
  /** 보는 위치가 바뀔 때 ({ id, step }) — App 이 배치에 저장한다 */
  onView: PropTypes.func,
  /** (text) => void — 에이전트 패널을 열고 입력창을 채운다(App.askAssistant). 없으면 「채팅에서 이어 하기」 를 숨긴다 */
  onAskAssistant: PropTypes.func,
  /** 채팅 에이전트가 가리킨 작업 { jobId, step, open, nonce } — nonce 가 바뀔 때마다 반응한다 */
  focus: PropTypes.shape({ jobId: PropTypes.string, step: PropTypes.string, open: PropTypes.bool, nonce: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(SqlTuningModal);
